import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/useAuth';
import { safariTips } from '../../../shared/components/ui/LoadingState.messages';
import { GoogleOAuthButton } from './GoogleOAuthButton';
import { Logo } from '../../../shared/components/ui/Logo';
import { Button } from '../../../shared/components/ui/Button';
import { Card } from '../../../shared/components/ui/Card';

function TipsCarousel() {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const timer = setInterval(
      () => setIdx((i: number) => (i + 1) % safariTips.length),
      8000
    );
    return () => clearInterval(timer);
  }, []);
  
  const tipRaw = safariTips[idx];
  const match = tipRaw.match(
    /^([\p{Emoji}\uD83D-\uDBFF\uDC00-\uDFFF\u2600-\u27BF]+)\s*(.*)$/u
  );
  const text = match ? match[2] : tipRaw;

  let dots: number[] = [];
  if (safariTips.length <= 3) {
    dots = Array.from({ length: safariTips.length }, (_, i) => i);
  } else {
    const prev = (idx - 1 + safariTips.length) % safariTips.length;
    const next = (idx + 1) % safariTips.length;
    dots = [prev, idx, next];
  }

  return (
    <div className="text-left min-h-[100px] flex flex-col items-start justify-center w-full">
      <p className="text-sm font-medium text-text-primary mb-4 animate-fade-in leading-relaxed w-full">
        {text}
      </p>
      <div className="flex gap-2 justify-start text-left w-full">
        {dots.map((tipIdx: number, i: number) => (
          <span
            key={i}
            className={`inline-block w-2 h-2 rounded-full transition-all duration-300 ${
              tipIdx === idx ? 'bg-primary-500 w-6' : 'bg-neutral-300'
            }`}
          ></span>
        ))}
      </div>
    </div>
  );
}

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  
  const urlParams = new URLSearchParams(location.search);
  const from = location.state?.from || urlParams.get('from') || '/dashboard';
  const message = location.state?.message || urlParams.get('message');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password, formData.rememberMe);
    } catch (error) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
        <div className="min-h-screen bg-gradient-to-br from-background-primary via-background-secondary to-background-primary">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/30 to-accent-teal-50/30"></div>
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-5xl mx-auto">
                      {/* Main Container */}  
            <div className="relative lg:bg-gradient-to-br lg:from-primary-50 lg:via-white lg:to-accent-coral-50 lg:rounded-3xl lg:shadow-3xl lg:overflow-hidden min-h-[600px]">
              {/* 2-Column Layout */}
              <div className="hidden lg:grid lg:grid-cols-2 gap-0 min-h-[600px]">
              {/* Column 1: Logo and Content */}
              <div className="hidden lg:flex flex-col items-start justify-between p-12 lg:p-16 w-full">
                <div className="flex-1 flex flex-col justify-start">
                  {/* Logo */}
                  <div className="mb-8">
                    <Logo variant="default" width={60} height={30} />
                  </div>
                  
                  {/* Main Content */}
                  <div className="mb-12">
                    <h1 className="text-4xl lg:text-3xl font-bold text-text-primary mb-6 leading-tight">
                      Fast, Efficient and Productive
                    </h1>
                    <p className="text-lg text-text-secondary leading-relaxed max-w-2xl">
                      Experience the best safari adventures in Tanzania with our comprehensive platform connecting travelers, tour operators, and transport providers.
                    </p>
                  </div>

                  {/* Tips Carousel */}
                  <div className="">
                    <TipsCarousel />
                  </div>
                </div>

                {/* Footer Links */}
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-6 text-sm">
                    <a href="/faq" className="text-primary-500 hover:text-primary-600 font-medium transition-colors">FAQ</a>
                    <a href="/privacy" className="text-primary-500 hover:text-primary-600 font-medium transition-colors">Privacy Policy</a>
                    <a href="/contact" className="text-primary-500 hover:text-primary-600 font-medium transition-colors">Contact Us</a>
                  </div>
                </div>
              </div>

              {/* Column 2: Sign-In Form Card (spans both remaining columns) */}
              <div className="col-span-1 flex items-center justify-center p-4 lg:p-8">
                <Card className="p-6 shadow-3xl border-0 w-full max-w-[500px] mr-10  ml-0">
                {/* Mobile Logo */}
                <div className="flex items-center justify-center mb-8 lg:hidden">
                  <Logo variant="default" width={120} height={40} />
                </div>

                {/* Header */}
                <div className="text-left mb-4">
                  <h1 className="text-lg font-bold text-text-primary mb-1 text-left">
                    Sign In
                  </h1>
                  <p className="text-text-secondary text-xs text-left">
                    to your account
                  </p>
                </div>

                {/* Message Alert */}
                {message && (
                  <Card className="mb-4 p-2 bg-accent-blue-50 border-accent-blue-200">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-accent-blue-500 rounded-full"></div>
                      <p className="text-accent-blue-700 text-xs font-medium">
                        {message}
                      </p>
                    </div>
                  </Card>
                )}

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-3">
                  {/* Email Field */}
                  <div>
                    <label className="block text-xs font-semibold text-text-primary mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={e =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="w-full pl-12 pr-4 py-2.5 border border-border-primary rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all duration-200 bg-background-secondary hover:bg-white"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    <label className="block text-xs font-semibold text-text-primary mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={e =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        className="w-full pl-12 pr-12 py-2.5 border border-border-primary rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all duration-200 bg-background-secondary hover:bg-white"
                        placeholder="Enter your password"
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5 text-neutral-500" />
                        ) : (
                          <Eye className="w-5 h-5 text-neutral-500" />
                        )}
                      </button>
                    </div>
                    {error && (
                      <p className="mt-2 text-sm text-accent-error-600 font-medium">
                        {error}
                      </p>
                    )}
                  </div>

                  {/* Forgot Password */}
                  <div className="flex justify-end">
                    <a
                      href="/forgot-password"
                      className="text-xs text-primary-500 hover:text-primary-600 font-medium transition-colors"
                    >
                      Forgot Password?
                    </a>
                  </div>

                  {/* Sign In Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    loading={loading}
                    fullWidth
                    className="mt-4 text-xs"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>

                  {/* Divider */}
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border-primary"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-4 bg-white text-text-tertiary font-medium">
                        Or with
                      </span>
                    </div>
                  </div>

                  {/* Google OAuth */}
                  <div className="flex w-full items-center justify-center">
                    <GoogleOAuthButton />
                  </div>

                  {/* Sign Up Link */}
                  <div className="text-center">
                    <p className="text-text-secondary text-xs">
                      Not a member yet?{' '}
                      <a href="/register" className="text-primary-500 hover:text-primary-600 font-semibold transition-colors">Sign Up</a>
                    </p>
                  </div>
                </form>
              </Card>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden flex items-center justify-center p-2 min-h-[600px]">
            <Card className="p-6 shadow-3xl border-0 w-full max-w-[400px]">

              {/* Header */}
              <div className="text-left mb-4">
                <h1 className="text-lg font-bold text-text-primary mb-1 text-left">
                  Sign In
                </h1>
                <p className="text-text-secondary text-xs text-left">
                  to your account
                </p>
              </div>

              {/* Message Alert */}
              {message && (
                <Card className="mb-4 p-2 bg-accent-blue-50 border-accent-blue-200">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-accent-blue-500 rounded-full"></div>
                    <p className="text-accent-blue-700 text-xs font-medium">
                      {message}
                    </p>
                  </div>
                </Card>
              )}

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Email Field */}
                <div>
                  <label className="block text-xs font-semibold text-text-primary mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full pl-12 pr-4 py-2.5 border border-border-primary rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all duration-200 bg-background-secondary hover:bg-white"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label className="block text-xs font-semibold text-text-primary mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={e =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      className="w-full pl-12 pr-12 py-2.5 border border-border-primary rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all duration-200 bg-background-secondary hover:bg-white"
                      placeholder="Enter your password"
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5 text-neutral-500" />
                      ) : (
                        <Eye className="w-5 h-5 text-neutral-500" />
                      )}
                    </button>
                  </div>
                  {error && (
                    <p className="mt-2 text-sm text-accent-error-600 font-medium">
                      {error}
                    </p>
                  )}
                </div>

                {/* Forgot Password */}
                <div className="flex justify-end">
                  <a
                    href="/forgot-password"
                    className="text-xs text-primary-500 hover:text-primary-600 font-medium transition-colors"
                  >
                    Forgot Password?
                  </a>
                </div>

                {/* Sign In Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  loading={loading}
                  fullWidth
                  className="mt-4 text-xs"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>

                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border-primary"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-4 bg-white text-text-tertiary font-medium">
                      Or with
                    </span>
                  </div>
                </div>

                {/* Google OAuth */}
                <div className="flex w-full items-center justify-center">
                  <GoogleOAuthButton />
                </div>

                {/* Sign Up Link */}
                <div className="text-center">
                  <p className="text-text-secondary text-xs">
                    Not a member yet?{' '}
                    <a href="/register" className="text-primary-500 hover:text-primary-600 font-semibold transition-colors">Sign Up</a>
                  </p>
                </div>
              </form>
            </Card>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};
